import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { track } from '@/lib/observability';
import { toast } from '@/lib/toast';
import { authKeys } from './queries';
import { stepUpHeaders } from './stepup';

/**
 * REAL impersonation (server `POST /api/admin/impersonate/:userId` + `/stop`,
 * gated `users.impersonate` + step-up). `start` swaps the session cookie to act
 * AS the target; because that single cookie is shared, the operator then leaves
 * the admin app for the office app (VITE_OFFICE_APP_URL) experiencing the product
 * as the target. The banner (driven by `/me` → `impersonator`) + `stop` (which
 * only needs the active session) bring the admin back.
 *
 * FIX(admin-impersonation-banner-source): the persisted zustand store that used
 * to mirror "who am I impersonating" is gone — the server's `/me` is the only
 * source. Before: start/stop wrote a localStorage store the banner read. After:
 * both invalidate the session queries so `/me` (and every gate) re-reads the
 * swapped cookie.
 */

interface StartTarget {
  userId: string;
  userName: string;
  workspaceName?: string;
}

const officeAppUrl = (import.meta.env.VITE_OFFICE_APP_URL as string | undefined)?.trim();

export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: StartTarget) => {
      await apiClient.post(`/api/admin/impersonate/${encodeURIComponent(t.userId)}`, undefined, {
        headers: stepUpHeaders(),
      });
      return t;
    },
    onSuccess: (t) => {
      // The cookie now names the target: everything cached was the admin's view.
      void qc.invalidateQueries();
      track({ action: 'workspaces:impersonate.start', target: t.userId, result: 'success' });
      toast.warning('Impersonation started', { description: `Acting as ${t.userName}` });
      // The session cookie now belongs to the target. Leave the admin app for the
      // office app (where the operator experiences the product as the user). If no
      // office URL is configured, stay and rely on the banner + "End impersonation".
      if (officeAppUrl) window.location.assign(officeAppUrl);
    },
    onError: () => {
      toast.error('Could not start impersonation', {
        description: 'Re-authenticate and try again.',
      });
    },
  });
}

export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/api/admin/impersonate/stop'),
    onSuccess: () => {
      // The admin session is restored — refetch identity (and everything read
      // as the target) so gates and data reflect the admin again.
      void qc.invalidateQueries({ queryKey: authKeys.me });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== authKeys.me[0] });
      track({ action: 'workspaces:impersonate.stop', result: 'success' });
      toast.success('Stopped impersonation');
    },
    onError: () => {
      toast.error('Could not end impersonation', { description: 'Please log in again.' });
    },
  });
}
